import styled from "styled-components";
import useLessonInfo from "features/teacher/hooks/lesson/useLessonInfo";
import Refresh from "shared/components/Refresh";
import { hw, scrollbar } from "features/homework/homeworkUi";

interface ClassInfoProps {
    clickedLesson: string;
}

const LessonInfo = ({ clickedLesson }: ClassInfoProps) => {
    const { lesson, onRefreshClick } = useLessonInfo(clickedLesson);

    if (!clickedLesson) {
        return (
            <Container>
                <Header>
                    <div>
                        <Title>학급 정보</Title>
                        <Sub>오른쪽 목록에서 학급을 선택하면 상세 정보가 표시됩니다.</Sub>
                    </div>
                </Header>
                <Empty>
                    <EmptyTitle>학급을 선택하세요</EmptyTitle>
                    <EmptyText>학급을 고르면 담당 교사·요일·시간을 확인할 수 있습니다.</EmptyText>
                </Empty>
            </Container>
        );
    }

    const rows = [
        { label: "학급명", value: lesson.name },
        { label: "담당교사", value: lesson.teacher },
        { label: "수업요일", value: lesson.days },
        { label: "수업시간", value: lesson.time },
        { label: "특이사항", value: lesson.etc, wide: true },
    ];

    return (
        <Container>
            <Header>
                <div>
                    <Title>학급 정보</Title>
                    <Sub>{lesson.name || "선택한 학급"}의 수업 정보입니다.</Sub>
                </div>
                <Refresh onRefreshClick={onRefreshClick} />
            </Header>
            <Grid>
                {rows.map((row) => (
                    <Field key={row.label} $wide={row.wide}>
                        <Label>{row.label}</Label>
                        <Value>{row.value || "-"}</Value>
                    </Field>
                ))}
            </Grid>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    min-width: 0;
    min-height: 0;
    height: 100%;
    padding: 20px 22px;
    background: ${hw.surface};
    border: 1px solid ${hw.border};
    border-radius: 16px;
    box-shadow: ${hw.shadow};
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 12px;
    padding-bottom: 14px;
    border-bottom: 1px solid ${hw.border};
    flex-shrink: 0;
`;

const Title = styled.h2`
    margin: 0;
    color: ${hw.text};
    font-size: 16px;
    font-weight: 700;
    letter-spacing: -0.02em;
`;

const Sub = styled.p`
    margin: 4px 0 0;
    font-size: 13px;
    color: ${hw.textMuted};
`;

const Grid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    overflow: auto;
    min-height: 0;
    align-content: start;
    ${scrollbar}

    @media (max-width: 720px) {
        grid-template-columns: 1fr;
    }
`;

const Field = styled.div<{ $wide?: boolean }>`
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 14px 16px;
    background: ${hw.bg};
    border: 1px solid ${hw.border};
    border-radius: 12px;
    min-width: 0;
    grid-column: ${props => props.$wide ? "1 / -1" : "auto"};
`;

const Label = styled.div`
    font-size: 12px;
    font-weight: 600;
    color: ${hw.textMuted};
`;

const Value = styled.div`
    font-size: 15px;
    font-weight: 600;
    color: ${hw.text};
    word-break: break-word;
`;

const Empty = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    text-align: center;
    min-height: 240px;
`;

const EmptyTitle = styled.div`
    font-size: 16px;
    font-weight: 700;
    color: ${hw.text};
`;

const EmptyText = styled.div`
    font-size: 13px;
    color: ${hw.textMuted};
    line-height: 1.5;
`;

export default LessonInfo;
